import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, FileText, Phone, Mail, MapPin, Save, Plus, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CRMCustomerDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCustomer(); }, [id]);

  const fetchCustomer = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const res = await axios.get(`${API}/customers/${id}`, { headers });
      setCustomer(res.data);
      setForm(res.data);
    } catch { toast.error("Kunde nicht gefunden"); navigate("/app/customers"); }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      await axios.patch(`${API}/customers/${id}`, {
        name: form.name, email: form.email, phone: form.phone,
        pflegegrad: form.pflegegrad, notes: form.notes,
        address_street: form.address_street, address_plz: form.address_plz,
        address_city: form.address_city, insurance_number: form.insurance_number,
        date_of_birth: form.date_of_birth,
      }, { headers });
      toast.success("Kunde aktualisiert");
      setEditing(false);
      fetchCustomer();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const formatDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-cyan-700 border-t-transparent rounded-full" /></div>;
  if (!customer) return null;

  return (
    <div data-testid="crm-customer-detail">
      <button onClick={() => navigate("/app/customers")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-cyan-700 mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Zurück zur Kundenliste
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900">{customer.name}</h1>
          <p className="text-sm text-slate-500">{customer.email} | {customer.phone}</p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <Button onClick={handleSave} data-testid="save-customer-btn" className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl"><Save className="h-4 w-4 mr-1" /> Speichern</Button>
          ) : (
            <Button onClick={() => setEditing(true)} variant="outline" className="rounded-xl">Bearbeiten</Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Customer info */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200/60 p-5">
            <h3 className="font-semibold text-slate-800 mb-4">Kundendaten</h3>
            <div className="space-y-3">
              {[
                { label: "Name", key: "name" },
                { label: "E-Mail", key: "email" },
                { label: "Telefon", key: "phone" },
                { label: "Pflegegrad", key: "pflegegrad" },
                { label: "Geburtsdatum", key: "date_of_birth" },
                { label: "Versichertennr.", key: "insurance_number" },
                { label: "Straße", key: "address_street" },
                { label: "PLZ", key: "address_plz" },
                { label: "Ort", key: "address_city" },
              ].map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-slate-500">{f.label}</Label>
                  {editing ? (
                    <Input value={form[f.key] || ""} onChange={(e) => setForm({...form, [f.key]: e.target.value})} className="h-9 rounded-lg text-sm mt-0.5" />
                  ) : (
                    <p className="text-sm font-medium text-slate-800">{customer[f.key] || "—"}</p>
                  )}
                </div>
              ))}
              <div>
                <Label className="text-xs text-slate-500">Notizen</Label>
                {editing ? (
                  <textarea value={form.notes || ""} onChange={(e) => setForm({...form, notes: e.target.value})} rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm mt-0.5 resize-none" />
                ) : (
                  <p className="text-sm text-slate-600">{customer.notes || "Keine Notizen"}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Appointments + Beratungsboegen */}
        <div className="lg:col-span-2 space-y-4">
          {/* Appointments */}
          <div className="bg-white rounded-xl border border-slate-200/60 p-5">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-cyan-700" /> Termine</h3>
            {(customer.appointments || []).length === 0 ? (
              <p className="text-sm text-slate-400">Keine Termine</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {customer.appointments.map((apt) => (
                  <div key={apt.id} className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-slate-700">{formatDate(apt.date)} — {apt.time_slot} Uhr</p>
                      <p className="text-xs text-slate-500">{apt.consultation_type === "erstberatung" ? "Erstberatung" : apt.consultation_type === "folgeberatung" ? "Folgeberatung" : apt.consultation_type}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${apt.status === "confirmed" ? "bg-green-50 text-green-700" : apt.status === "completed" ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-700"}`}>
                        {apt.status === "confirmed" ? "Bestätigt" : apt.status === "completed" ? "Abgeschlossen" : apt.status}
                      </span>
                      <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => navigate(`/app/beratung/new/${apt.id}`)}>
                        <FileText className="h-3 w-3 mr-1" /> Bogen
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Beratungsboegen */}
          <div className="bg-white rounded-xl border border-slate-200/60 p-5">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><FileText className="h-4 w-4 text-green-600" /> Beratungsbögen</h3>
            {(customer.beratungsboegen || []).length === 0 ? (
              <p className="text-sm text-slate-400">Noch keine Beratungsbögen erstellt</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {customer.beratungsboegen.map((b) => (
                  <div key={b.id} className="flex items-center justify-between py-3 hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors">
                    <div className="cursor-pointer flex-1" onClick={() => navigate(`/app/beratung/${b.id}`)}>
                      <p className="text-sm font-medium text-slate-700">Beratungsbogen vom {b.consultation_date || "—"}</p>
                      <p className="text-xs text-slate-500">Status: {b.status === "draft" ? "Entwurf" : b.status === "completed" ? "Abgeschlossen" : b.status}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${b.status === "completed" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                        {b.status === "completed" ? "Fertig" : "Entwurf"}
                      </span>
                      <Button size="sm" variant="outline" className="rounded-lg text-xs h-9 active:scale-95" onClick={async () => {
                        try {
                          const headers = { Authorization: `Bearer ${user.token}` };
                          const res = await axios.get(`${API}/beratungsboegen/${b.id}/pdf`, { headers, responseType: "blob" });
                          const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = `Beratungsbogen_${b.consultation_date || ""}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        } catch { toast.error("Fehler beim PDF-Export"); }
                      }}>
                        <Download className="h-3 w-3 mr-1" /> PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
